import React, { useEffect, useState, useRef } from 'react';

import { DataGrid } from '@mui/x-data-grid';
import { Card } from '@sunwisesoftware/sunwise-ui';
import { debounce, isEqual, isString } from 'lodash';
import PropTypes from 'prop-types';

import { CustomPagination } from 'common/components';
import { DEBOUNCE_TIME_500, TABLE_PAGE_SIZE_OPTIONS } from 'common/constants';

import BulkOperationsCard from './BulkOperationsCard';
import ProductColumns from './ProductColumns';

const ListItems = ({
    disabled,
    brands,
    fieldName,
    filterData,
    filterItems,
    isFetching,
    isFetchingBrands,
    items,
    paginationData,
    setFilterData,
    setValue,
    value,
}) => {
    const [rowCount, setRowCount] = useState(paginationData?.rowCount || 0);
    const [selectionModel, setSelectionModel] = useState([]);
    const [currentFilterData, setCurrentFilterData] = useState({});

    const handleOnFilter = useRef(
        debounce((filter) => filterItems(filter), DEBOUNCE_TIME_500),
    ).current;

    const handleOnChangeSearch = (event) => {
        setFilterData({
            ...filterData,
            page: 0,
            searchText: event.target.value,
        });
    };

    const handleOnChangeBrand = (value) => {
        const filter = { ...filterData, page: 0, brandName: value };
        setFilterData(filter);
    };

    const pageSizeOptions = TABLE_PAGE_SIZE_OPTIONS;

    const columns = ProductColumns();
    const getRowHeight = () => 'auto';
    const initialState = {
        pagination: {
            paginationModel: { pageSize: TABLE_PAGE_SIZE_OPTIONS[0] },
        },
    };
    const isRowSelectable = () => !disabled;
    const onPaginationModelChange = (model) =>
        setFilterData((prev) => ({ ...prev, ...model }));
    const onRowSelectionModelChange = (model) => setSelectionModel(model);
    const onSortModelChange = (sortModel) => {
        if (!sortModel[0]) return;
        setFilterData((prev) => ({
            ...prev,
            orderBy: sortModel[0].field.toLocaleLowerCase(),
            sortBy: sortModel[0].sort,
        }));
    };

    const slots = { pagination: CustomPagination };
    const slotProps = { pagination: { pageSizeOptions } };

    useEffect(() => {
        if (paginationData)
            setRowCount((prevRowCount) =>
                paginationData?.total_rows !== undefined
                    ? paginationData?.total_rows
                    : prevRowCount,
            );
    }, [paginationData?.total_rows, setRowCount]);

    useEffect(() => {
        if (!isEqual(currentFilterData, filterData)) {
            handleOnFilter(filterData);
            setCurrentFilterData(filterData);
        }
    }, [filterData]);

    useEffect(() => {
        if (value && isString(value)) setSelectionModel(value?.split(','));
    }, [value]);

    useEffect(() => {
        setValue(fieldName, selectionModel);
    }, [selectionModel]);

    return (
        <>
            <BulkOperationsCard
                disabled={disabled}
                brands={brands}
                filterData={filterData}
                handleOnChangeBrand={handleOnChangeBrand}
                handleOnChangeSearch={handleOnChangeSearch}
                isFetchingBrands={isFetchingBrands}
            />

            <Card>
                <Card.Body>
                    <DataGrid
                        checkboxSelection
                        columns={columns}
                        disableColumnMenu
                        disableRowSelectionOnClick
                        getRowHeight={getRowHeight}
                        initialState={initialState}
                        isRowSelectable={isRowSelectable}
                        loading={isFetching}
                        onPaginationModelChange={onPaginationModelChange}
                        onRowSelectionModelChange={onRowSelectionModelChange}
                        onSortModelChange={onSortModelChange}
                        pageSizeOptions={pageSizeOptions}
                        pagination
                        paginationMode="server"
                        rowCount={rowCount}
                        rowSelectionModel={selectionModel}
                        rows={items}
                        slotProps={slotProps}
                        slots={slots}
                        sortingMode="server"
                    />
                </Card.Body>
            </Card>
        </>
    );
};

ListItems.propTypes = {
    disabled: PropTypes.bool,
    brands: PropTypes.array,
    fieldName: PropTypes.string,
    filterData: PropTypes.object,
    filterItems: PropTypes.func,
    isFetching: PropTypes.bool,
    isFetchingBrands: PropTypes.bool,
    items: PropTypes.array,
    paginationData: PropTypes.object,
    setFilterData: PropTypes.func,
    setValue: PropTypes.func,
    value: PropTypes.string,
};

export default ListItems;
